import { v4 as uuidv4 } from 'uuid';
import { TableContent } from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/Table/Table.utils';
import { PricingTableContent } from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/PricingTable/PricingTable.utils';
import { ContractInfoTableContent } from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/ContractInfoTable/ContractInfoTable.utils';
import { docGeniusPalette } from 'styles/partials/colors';

export enum TextOverflowRole {
  CONTENT = 'CONTENT',
  HEADER = 'HEADER',
  FOOTER = 'FOOTER',
}

export interface CheckboxItem {
  id: string;
  label: string;
  checked: boolean;
}

export interface DraggableComponentProps {
  currentElement: Element;
}
export interface DropdownOptionItem {
  id: string;
  option: string;
  value: string;
}

export enum RecipientRole {
  VIEWER = 'VIEWER',
  SIGNER = 'SIGNER',
  COPY = 'COPY',
}

export interface Recipient {
  id: string;
  email: string;
  fullName: string;
  role: RecipientRole;
  color?: string;
  elementsIds?: string[];
  orderId?: number;
}

export enum ElementType {
  Button = 'BUTTON',
  CheckBox = 'CHECKBOX',
  Date = 'DATE',
  Divider = 'DIVIDER',
  Dropdown = 'DROPDOWN',
  Image = 'IMAGE',
  RadioButton = 'RADIO_BUTTON',
  Signature = 'SIGNATURE',
  Text = 'TEXT',
  TextField = 'TEXTFIELD',
  Spacer = 'SPACER',
  Video = 'VIDEO',
  Table = 'TABLE',
  Initials = 'INITIALS',
  PricingTable = 'PRICING_TABLE',
  Header = 'HEADER',
  Footer = 'FOOTER',
  BillTo = 'BILL_TO',
  ContactInfo = 'CONTACT_INFO',
  ShipTo = 'SHIP_TO',
  QuoteAcceptanceInfoTable = 'QUOTE_ACCEPTANCE_INFO',
  ContractInfoTable = 'CONTRACT_INFO_TABLE',
  ApprovalTable = 'APPROVAL_TABLE',
}
interface Logo {
  id: number | null;
  name: string;
  src: string;
}

interface fileImage {
  id: number | null;
  name: string;
}

export type ButtonElement = {
  id: string;
  type: ElementType.Button;
  content: {
    value: string;
  };
  style: {
    [key: string]: string;
  };
};

export type HeaderElement = {
  id: string;
  type: ElementType.Header;
  content: {
    text: string;
    hasBackgroundColor: boolean;
    hasHeaderDetails: boolean;
    hasLogo: boolean;
    backgroundImage: fileImage;
    hasBackgroundImage: boolean;
    logo: Logo;
  };
  style: {
    backgroundImageOpacity: number;
    backgroundCover: string;
    backgroundPosition: string;
    backgroundColor: string;
    fontColor: string;
    labelFont: string;
    labelFontSize: string;
    addSheetPadding: boolean;
  };
};

export type FooterElement = {
  id: string;
  type: ElementType.Footer;
  content: {
    hasFooterText: boolean;
    text: string;
    hasBackgroundColor: boolean;
    hasPageNumbering: boolean;
    hasBackgroundImage: boolean;
    backgroundImage: fileImage;
    hasLogo: boolean;
    logo: fileImage;
  };
  style: {
    textFontSize: string;
    textFont: string;
    textColor: string;
    textAlignment: 'center' | 'left' | 'right';
    backgroundColor: string;
    labelFont: string;
    labelFontSize: string;
    backgroundImageOpacity: number;
    fontColor: string;
    backgroundCover: string;
    backgroundPosition: string;
  };
};

export type VideoElement = {
  id: string;
  type: ElementType.Video;
  content: {
    src: string;
  };
  style: {
    [key: string]: string;
  };
};

export type TextElement = {
  id: string;
  type: ElementType.Text;
  content: {
    value: string;
    overflowRole?: TextOverflowRole;
    maxTextRows?: number;
    maxHeight?: number;
    paddingX?: number;
    paddingY?: number;
    hideFooterOnLastPage?: boolean;
  };
  style: {
    [key: string]: string;
  };
};

export type ImageElement = {
  id: string;
  type: ElementType.Image;
  content: {
    id: number | null;
  };
  style: {
    justifyContent: string;
    padding: string;
    width: string;
  };
};

export type TextFieldElement = {
  id: string;
  type: ElementType.TextField;
  content: {
    label: string;
    placeholder: string;
    required: boolean;
    value: string;
  };
  style: {
    labelFont: string;
    labelFontSize: string;
    labelColor: string;
  };
  recipient: Recipient;
};

export type DividerElement = {
  id: string;
  type: ElementType.Divider;
  content: {
    [key: string]: string;
  };
  style: {
    thickness: string;
    color: string;
    borderRadius: string;
    size: string;
  };
};

export type CheckBoxElement = {
  id: string;
  type: ElementType.CheckBox;
  content: {
    elements: CheckboxItem[];
    groupTooltip: string;
    groupDirection: 'vertical' | 'horizontal';
  };
  style: {
    [key: string]: string;
  };
  recipient: Recipient;
};

export type RadioButtonElement = {
  id: string;
  type: ElementType.RadioButton;
  content: {
    elements: string[];
    required: boolean;
    groupDirection: 'vertical' | 'horizontal';
  };
  style: {
    [key: string]: string;
  };
  recipient: Recipient;
};

export type DropdownElement = {
  id: string;
  type: ElementType.Dropdown;
  content: {
    defaultOption: string;
    elements: DropdownOptionItem[];
    labelText: string;
    placeholder: string;
    required: boolean;
    value: string;
  };
  style: {
    borderRadius: string;
    height: string;
    size: string;
  };
  recipient: Recipient;
};

export type DateElement = {
  id: string;
  type: ElementType.Date;
  content: {
    labelText: string;
  };
  style: {
    borderRadius: string;
    height: string;
    size: string;
  };
  recipient: Recipient;
};

export type SignatureElement = {
  id: string;
  type: ElementType.Signature;
  content: {
    labelText: string;
    required: boolean;
  };
  style: {
    justifyContent: string;
  };
  recipient: Recipient;
};

export type SpacerElement = {
  id: string;
  type: ElementType.Spacer;
  content: {
    height: number;
  };
  style: {
    [key: string]: string;
  };
};

export type TableElement = {
  id: string;
  type: ElementType.Table;
  content: TableContent;
  style: {
    [key: string]: string;
  };
};

export type InitialsElement = {
  id: string;
  type: ElementType.Initials;
  content: {
    labelText: string;
    required: boolean;
  };
  style: {
    justifyContent: string;
  };
  recipient: Recipient;
};

export type PricingTableElement = {
  id: string;
  type: ElementType.PricingTable;
  content: PricingTableContent;
  style: {
    [key: string]: string;
  };
};

export type BillToElement = {
  id: string;
  type: ElementType.BillTo;
  content: {
    billTo: string;
    address: string;
    city: string;
    country: string;
  };
  style: {
    justifyContent: string;
  };
};

export type ContactInfoElement = {
  id: string;
  type: ElementType.ContactInfo;
  content: {
    name: string;
    number: string;
    email: string;
    fax: string;
  };
  style: {
    justifyContent: string;
  };
};

export type ShipToElement = {
  id: string;
  type: ElementType.ShipTo;
  content: {
    shipTo: string;
    address: string;
    city: string;
    country: string;
  };
  style: {
    justifyContent: string;
  };
};

export type QuoteAcceptanceInfoTableElement = {
  id: string;
  type: ElementType.QuoteAcceptanceInfoTable;
  content: {
    acceptanceInfo: {
      title: string;
      name: string;
      date: {
        justifyContent: string;
        required: boolean;
      };
      signature: {
        justifyContent: string;
        required: boolean;
      };
    };
    table: TableContent;
    activeSidebar: 'acceptanceInfo' | 'date' | 'signature';
  };
  style: {
    [key: string]: string;
  };
  recipient: Recipient;
};

export type ContractInfoTableElement = {
  id: string;
  type: ElementType.ContractInfoTable;
  content: ContractInfoTableContent;
  style: {
    [key: string]: string;
  };
};

export type ApprovalTableElement = {
  id: string;
  type: ElementType.ApprovalTable;
  recipients: {
    customerRecipient: Recipient;
    companyRecipient: Recipient;
  };
  content: {
    approvalInfo: {
      customerSignedDate: {
        justifyContent: string;
        required: boolean;
      };
      companySignedDate: {
        justifyContent: string;
        required: boolean;
      };
      customerSignature: {
        justifyContent: string;
        required: boolean;
      };
      companySignature: {
        justifyContent: string;
        required: boolean;
      };
    };
    table: TableContent;
    activeSidebar:
      | 'approvalTable'
      | 'customerSignedDate'
      | 'companySignedDate'
      | 'customerSignature'
      | 'companySignature';
  };
  style: {
    [key: string]: string;
  };
};

export type Element =
  | ButtonElement
  | CheckBoxElement
  | DateElement
  | DividerElement
  | DropdownElement
  | ImageElement
  | RadioButtonElement
  | SignatureElement
  | SpacerElement
  | TextElement
  | TextFieldElement
  | RadioButtonElement
  | VideoElement
  | InitialsElement
  | PricingTableElement
  | TableElement
  | HeaderElement
  | BillToElement
  | ContactInfoElement
  | ShipToElement
  | FooterElement
  | QuoteAcceptanceInfoTableElement
  | ContractInfoTableElement
  | ContractInfoTableElement
  | ApprovalTableElement
  | ContractInfoTableElement;

type ElementsTemplate = {
  [ElementType.Button]: ButtonElement;
  [ElementType.CheckBox]: CheckBoxElement;
  [ElementType.Date]: DateElement;
  [ElementType.Divider]: DividerElement;
  [ElementType.Dropdown]: DropdownElement;
  [ElementType.Image]: ImageElement;
  [ElementType.RadioButton]: RadioButtonElement;
  [ElementType.Signature]: SignatureElement;
  [ElementType.Spacer]: SpacerElement;
  [ElementType.Text]: TextElement;
  [ElementType.TextField]: TextFieldElement;
  [ElementType.Video]: VideoElement;
  [ElementType.Table]: TableElement;
  [ElementType.Initials]: InitialsElement;
  [ElementType.PricingTable]: PricingTableElement;
  [ElementType.Header]: HeaderElement;
  [ElementType.Footer]: FooterElement;
  [ElementType.BillTo]: BillToElement;
  [ElementType.ContactInfo]: ContactInfoElement;
  [ElementType.ShipTo]: ShipToElement;
  [ElementType.QuoteAcceptanceInfoTable]: QuoteAcceptanceInfoTableElement;
  [ElementType.ContractInfoTable]: ContractInfoTableElement;
  [ElementType.ApprovalTable]: ApprovalTableElement;
};

//  TODO: set the active user as default recipient
export const elementsTemplate: ElementsTemplate = {
  [ElementType.Header]: {
    id: '',
    type: ElementType.Header,
    content: {
      hasBackgroundColor: true,
      hasHeaderDetails: true,
      hasLogo: false,
      hasBackgroundImage: false,
      logo: {
        id: null,
        src: '',
        name: '',
      },
      text: 'Untitled',
      backgroundImage: {
        id: null,
        name: '',
      },
    },
    style: {
      backgroundColor: '#ffffff',
      labelFont: 'Calibri',
      labelFontSize: '18px',
      backgroundCover: 'cover',
      backgroundPosition: 'center',
      backgroundImageOpacity: 1,
      fontColor: 'black',
      addSheetPadding: true,
    },
  },
  [ElementType.Footer]: {
    id: '',
    type: ElementType.Footer,
    content: {
      hasFooterText: false,
      text: 'Add text',
      hasBackgroundColor: true,
      hasPageNumbering: true,
      hasBackgroundImage: false,
      hasLogo: false,
      logo: {
        id: null,
        name: '',
      },
      backgroundImage: {
        id: null,
        name: '',
      },
    },
    style: {
      textAlignment: 'center',
      textColor: 'black',
      textFont: 'Calibri',
      textFontSize: '18px',
      backgroundColor: '#ffffff',
      labelFont: 'Calibri',
      labelFontSize: '18px',
      backgroundImageOpacity: 1,
      fontColor: 'black',
      backgroundCover: 'cover',
      backgroundPosition: 'center',
    },
  },
  [ElementType.Button]: {
    id: '',
    type: ElementType.Button,
    content: {
      value: 'Click Here',
    },
    style: {},
  },
  [ElementType.Video]: {
    id: '',
    type: ElementType.Video,
    content: { src: '' },
    style: {
      align: 'start',
      width: '560',
      height: '315',
      justifyContent: 'left',
    },
  },
  [ElementType.Text]: {
    id: '',
    type: ElementType.Text,
    content: {
      value: '',
      paddingX: 12,
      paddingY: 8,
    },
    style: {},
  },
  [ElementType.Image]: {
    id: '',
    type: ElementType.Image,
    content: {
      id: null,
    },
    style: {
      justifyContent: 'center',
      width: '100%',
      padding: '',
    },
  },
  [ElementType.TextField]: {
    id: '',
    type: ElementType.TextField,
    content: {
      label: '',
      placeholder: '',
      required: false,
      value: '',
    },
    style: {
      labelFont: 'Roboto',
      labelFontSize: '16px',
      labelColor: '',
    },
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.Divider]: {
    id: '',
    type: ElementType.Divider,
    content: {},
    style: {
      thickness: '2px',
      color: 'black',
      borderRadius: '5px',
      size: '100%',
    },
  },
  [ElementType.CheckBox]: {
    id: '',
    type: ElementType.CheckBox,
    content: {
      elements: [{ id: uuidv4(), label: 'Label', checked: false }],
      groupTooltip: 'Checkbox group',
      groupDirection: 'horizontal',
    },
    style: {},
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.RadioButton]: {
    id: '',
    type: ElementType.RadioButton,
    content: {
      elements: ['Option 1', 'Option 2'],
      required: false,
      groupDirection: 'vertical',
    },
    style: {},
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.Dropdown]: {
    id: '',
    type: ElementType.Dropdown,
    content: {
      defaultOption: '',
      labelText: '',
      placeholder: '',
      required: false,
      value: '1',
      elements: [{ id: uuidv4(), option: 'Option', value: 'Value' }],
    },
    style: {
      borderRadius: '5px',
      height: '2px',

      size: '100%',
    },
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.Date]: {
    id: '',
    type: ElementType.Date,
    content: {
      labelText: 'Sign date.',
    },
    style: {
      borderRadius: '5px',
      height: '2px',
      size: '100%',
    },
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.Signature]: {
    id: '',
    type: ElementType.Signature,
    content: {
      labelText: 'Recipent Signature.',
      required: false,
    },
    style: {
      justifyContent: 'center',
    },
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.Spacer]: {
    id: '',
    type: ElementType.Spacer,
    content: {
      height: 40,
    },
    style: {},
  },
  [ElementType.Table]: {
    id: '',
    type: ElementType.Table,
    content: {
      columns: [
        { id: 'column-1', header: '', cellIds: ['cell-1', 'cell-2', 'cell-3'] },
        { id: 'column-2', header: '', cellIds: ['cell-4', 'cell-5', 'cell-6'] },
        { id: 'column-3', header: '', cellIds: ['cell-7', 'cell-8', 'cell-9'] },
      ],
      rows: [
        { id: 'row-1', cellIds: ['cell-1', 'cell-4', 'cell-7'] },
        { id: 'row-2', cellIds: ['cell-2', 'cell-5', 'cell-8'] },
        { id: 'row-3', cellIds: ['cell-3', 'cell-6', 'cell-9'] },
      ],
      cells: {
        'cell-1': { id: 'cell-1', content: '' },
        'cell-2': { id: 'cell-2', content: '' },
        'cell-3': { id: 'cell-3', content: '' },
        'cell-4': { id: 'cell-4', content: '' },
        'cell-5': { id: 'cell-5', content: '' },
        'cell-6': { id: 'cell-6', content: '' },
        'cell-7': { id: 'cell-7', content: '' },
        'cell-8': { id: 'cell-8', content: '' },
        'cell-9': { id: 'cell-9', content: '' },
      },
    },
    style: {},
  },
  [ElementType.Initials]: {
    id: '',
    type: ElementType.Initials,
    content: {
      labelText: 'Initials',
      required: false,
    },
    style: {
      justifyContent: 'center',
    },
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.PricingTable]: {
    id: '',
    type: ElementType.PricingTable,
    content: {
      activeProduct: null,
      columns: [
        { id: 'column-1', header: 'Product', cellIds: ['cell-1'] },
        { id: 'column-7', header: 'Description', cellIds: ['cell-19'], hidden: true },
        { id: 'column-2', header: 'List Price', cellIds: ['cell-4'] },
        { id: 'column-3', header: 'Sales Price', cellIds: ['cell-7'] },
        { id: 'column-4', header: 'Qty.', cellIds: ['cell-10'] },
        { id: 'column-5', header: 'Dis. (%)', cellIds: ['cell-13'] },
        { id: 'column-6', header: 'Total Price', cellIds: ['cell-16'] },
      ],
      rows: [{ id: 'row-1', cellIds: ['cell-1', 'cell-19', 'cell-4', 'cell-7', 'cell-10', 'cell-13', 'cell-16'] }],
      cells: {
        'cell-1': { id: 'cell-1', content: '' },
        'cell-19': { id: 'cell-19', content: '', hidden: true },
        'cell-4': { id: 'cell-4', content: '' },
        'cell-7': { id: 'cell-7', content: '' },
        'cell-10': { id: 'cell-10', content: '' },
        'cell-13': { id: 'cell-13', content: '' },
        'cell-16': { id: 'cell-16', content: '' },
      },
      resume: {
        subtotal: 0,
        discount: 0,
        totalPrice: 0,
        tax: 0,
        shippingHandling: 0,
        grandTotal: 0,
      },
      headerColor: docGeniusPalette.tableHeader,
      hideDiscountColumn: false,
      hideListPriceColumn: false,
      hideSummary: false,
      hideDescriptionColumn: true,
      showDescriptionAsImage: false,
    },
    style: {},
  },
  [ElementType.BillTo]: {
    id: '',
    type: ElementType.BillTo,
    content: {
      billTo: 'John Doe',
      address: 'Lorem Ipsum Avenue APT 1 12345',
      city: 'Chicago Illinois',
      country: 'US',
    },
    style: {
      justifyContent: 'left',
    },
  },
  [ElementType.ContactInfo]: {
    id: '',
    type: ElementType.ContactInfo,
    content: {
      name: 'Jane Doe',
      number: '9991234567',
      email: 'jane.doe@gmail.com',
      fax: '18008529001',
    },
    style: {
      justifyContent: 'left',
    },
  },
  [ElementType.ShipTo]: {
    id: '',
    type: ElementType.ShipTo,
    content: {
      shipTo: 'Jane Doe',
      address: 'Calle 60 num 123 x 57 y 59 Colonia Lorem Ipsum',
      city: '97000, Mérida, Yucatán',
      country: 'MX',
    },
    style: {
      justifyContent: 'left',
    },
  },
  [ElementType.QuoteAcceptanceInfoTable]: {
    id: '',
    type: ElementType.QuoteAcceptanceInfoTable,
    content: {
      acceptanceInfo: {
        title: '',
        name: '',
        date: {
          justifyContent: 'center',
          required: false,
        },
        signature: {
          justifyContent: 'center',
          required: false,
        },
      },
      table: {
        columns: [
          { id: 'column-1', header: 'Quote Acceptance Information', cellIds: ['cell-1', 'cell-2', 'cell-3', 'cell-4'] },
          { id: 'column-2', header: '', cellIds: ['cell-title', 'cell-name', 'cell-date', 'cell-signature'] },
        ],
        rows: [
          { id: 'row-1', cellIds: ['cell-1', 'cell-title'] },
          { id: 'row-2', cellIds: ['cell-2', 'cell-name'] },
          { id: 'row-3', cellIds: ['cell-3', 'cell-date'] },
          { id: 'row-4', cellIds: ['cell-4', 'cell-signature'] },
        ],
        cells: {
          'cell-1': { id: 'cell-1', content: 'Title:' },
          'cell-2': { id: 'cell-2', content: 'Name:' },
          'cell-3': { id: 'cell-3', content: 'Date:' },
          'cell-4': { id: 'cell-4', content: 'Signature:' },
          'cell-title': { id: 'cell-title', content: '' },
          'cell-name': { id: 'cell-name', content: '' },
          'cell-date': { id: 'cell-date', content: 'Date' },
          'cell-signature': { id: 'cell-signature', content: 'Signature' },
        },
      },
      activeSidebar: 'acceptanceInfo',
    },
    style: {},
    recipient: {
      id: '',
      email: '',
      fullName: '',
      role: RecipientRole.SIGNER,
    },
  },
  [ElementType.ContractInfoTable]: {
    id: '',
    type: ElementType.ContractInfoTable,
    content: {
      header: { id: 'column-1', header: 'Contract Information', cellIds: [] },
      rows: [
        { id: 'row-1', cellIds: ['cell-1', 'cell-2'] },
        { id: 'row-2', cellIds: ['cell-3', 'cell-4'] },
        { id: 'row-3', cellIds: ['cell-5', 'cell-6'] },
        { id: 'row-4', cellIds: ['cell-7', 'cell-8'] },
        { id: 'row-5', cellIds: ['cell-9', 'cell-10'] },
        { id: 'row-6', cellIds: ['cell-11', 'cell-12'], hidden: true },
        { id: 'row-7', cellIds: ['cell-13', 'cell-14'] },
        { id: 'row-8', cellIds: ['cell-15', 'cell-16'], hidden: true },
      ],
      cells: {
        'cell-1': { id: 'cell-1', content: 'Contract Owner:' },
        'cell-2': { id: 'cell-2', content: '' },
        'cell-3': { id: 'cell-3', content: 'Contract Number:' },
        'cell-4': { id: 'cell-2', content: '' },
        'cell-5': { id: 'cell-5', content: 'Contract Start Date:' },
        'cell-6': { id: 'cell-2', content: '' },
        'cell-7': { id: 'cell-7', content: 'Contract End Date:' },
        'cell-8': { id: 'cell-2', content: '' },
        'cell-9': { id: 'cell-9', content: 'Contract Terms (Months):' },
        'cell-10': { id: 'cell-2', content: '' },
        'cell-11': { id: 'cell-11', content: 'Special Terms:' },
        'cell-12': { id: 'cell-12', content: '' },
        'cell-13': { id: 'cell-13', content: 'Owner Expiration Notice:' },
        'cell-14': { id: 'cell-2', content: '' },
        'cell-15': { id: 'cell-15', content: 'Description:' },
        'cell-16': { id: 'cell-16', content: '' },
      },
      hideDescriptionRow: true,
      hideSpecialTermsRow: true,
    },
    style: {},
  },
  [ElementType.ApprovalTable]: {
    id: '',
    type: ElementType.ApprovalTable,
    content: {
      approvalInfo: {
        customerSignedDate: {
          justifyContent: 'center',
          required: false,
        },
        companySignedDate: {
          justifyContent: 'center',
          required: false,
        },
        customerSignature: {
          justifyContent: 'center',
          required: false,
        },
        companySignature: {
          justifyContent: 'center',
          required: false,
        },
      },
      table: {
        columns: [
          { id: 'column-1', header: 'Account Information', cellIds: ['cell-1'] },
          { id: 'column-2', header: '', cellIds: ['cell-4'] },
          { id: 'column-3', header: 'Company Information', cellIds: ['cell-7'] },
          { id: 'column-4', header: '', cellIds: ['cell-10'] },
        ],
        rows: [
          { id: 'row-1', cellIds: ['cell-1', 'cell-2', 'cell-3', 'cell-4'] },
          { id: 'row-2', cellIds: ['cell-5', 'cell-6'] },
          { id: 'row-3', cellIds: ['cell-7', 'cell-8', 'cell-9', 'cell-10'] },
          { id: 'row-4', cellIds: ['cell-11', 'cell-DATE_CUSTOMER', 'cell-12', 'cell-DATE_COMPANY'] },
          { id: 'row-5', cellIds: ['cell-13', 'cell-SIGNATURE_CUSTOMER', 'cell-15', 'cell-SIGNATURE_COMPANY'] },
        ],
        cells: {
          'cell-1': { id: 'cell-1', content: 'Account Name:' },
          'cell-2': { id: 'cell-2', content: '[Account.Name]' },
          'cell-3': { id: 'cell-3', content: 'Company Name:', rowSpan: 2 },
          'cell-4': { id: 'cell-4', content: '[Company.Name]', rowSpan: 2 },
          'cell-5': { id: 'cell-5', content: 'Customer Signed By:' },
          'cell-6': { id: 'cell-6', content: '[Customer.Signed.By]', backgroundColor: '#f6f8fb' },
          'cell-7': { id: 'cell-7', content: 'Customer Signed Title:' },
          'cell-8': { id: 'cell-8', content: '[Customer.Signed.Title]' },
          'cell-9': { id: 'cell-9', content: 'Company Signed By:' },
          'cell-10': { id: 'cell-10', content: '[Company.Signed.By]', backgroundColor: '#f6f8fb' },
          'cell-11': { id: 'cell-10', content: 'Customer Signed Date:' },
          'cell-DATE_CUSTOMER': { id: 'cell-DATE_CUSTOMER', content: 'Date', backgroundColor: '#f6f8fb' },
          'cell-12': { id: 'cell-11', content: 'Company Signed Date:' },
          'cell-DATE_COMPANY': { id: 'cell-DATE_COMPANY', content: 'Date' },
          'cell-13': { id: 'cell-13', content: 'Customer Signature:' },
          'cell-SIGNATURE_CUSTOMER': { id: 'cell-SIGNATURE_CUSTOMER', content: 'Signature' },
          'cell-15': { id: 'cell-15', content: 'Company Signature:' },
          'cell-SIGNATURE_COMPANY': { id: 'cell-SIGNATURE_COMPANY', content: 'Signature', backgroundColor: '#f6f8fb' },
        },
      },
      activeSidebar: 'approvalTable',
    },
    style: {},
    recipients: {
      companyRecipient: {
        id: '',
        email: '',
        fullName: '',
        role: RecipientRole.SIGNER,
      },
      customerRecipient: {
        id: '',
        email: '',
        fullName: '',
        role: RecipientRole.SIGNER,
      },
    },
  },
};

export enum ElementSide {
  Top = 'TOP',
  Bottom = 'BOTTOM',
  Left = 'LEFT',
  Right = 'RIGHT',
}

export enum PagePartType {
  Column = 'COLUMN',
  Row = 'ROW',
  Block = 'BLOCK',
  Element = 'ELEMENT',
  Page = 'PAGE',
}

export enum SidebarSectionsEnum {
  Blocks = 'BLOCKS',
  Variables = 'VARIABLES',
  TextEditor = "TEXT_EDITOR"
}

export enum documentStatus {
  Active = 'active',
  Draft = 'draft',
  Inactive = 'inactive',
  Signed = 'signed',
  Viewed = 'viewed',
  Sent = 'sent',
}
